(() => {
    'use strict';

    let MindElixir;
    let locales;
    let mindElixirLoad = null;
    const loadMindElixir = () => {
        if (!mindElixirLoad) {
            mindElixirLoad = Promise.all([
                lib.module('mind-elixir', 'MindElixir.js').then((m) => { MindElixir = m.default; }),
                lib.module('mind-elixir', 'i18n.js').then((m) => { locales = m; }),
            ]).catch((error) => {
                // Clear the memo so the next init retries instead of failing for the rest of the page session.
                mindElixirLoad = null;
                throw error;
            });
        }
        return mindElixirLoad;
    };

    // When the response carries a server error message, the default ajax notification shows it verbatim.
    // Passed as showErrorNotification so the specific message wins and the widget's own generic message is
    // shown by the error callback only when there is no specific one, keeping it to one notification either way.
    const hasServerErrorMessage = response => Boolean(response?.responseJSON?.errors?.[0]?.error);

    const syncOps = [
        'addChild',
        'copyNode',
        'copyNodes',
        'createArrow',
        'createSummary',
        'finishEdit',
        'finishEditArrowLabel',
        'finishEditSummary',
        'insertParent',
        'insertSibling',
        'moveDownNode',
        'moveNodeAfter',
        'moveNodeBefore',
        'moveNodeIn',
        'moveUpNode',
        'removeArrow',
        'removeNode',
        'removeNodes',
        'removeSummary',
    ];

    const sanitizeTopic = (topic) => {
        if (typeof topic !== 'string') {
            return topic;
        }
        // Remove zero-width and other invisible Unicode control characters
        // that are not legitimate whitespace (common paste artifacts from Office/web sources).
        return topic.replace(/[\u200B-\u200D\uFEFF\u00AD\u2028\u2029]/g, '').trim();
    };

    const getWidgetFromNotification = (notification) => {
        const widget = $('#widget-' + notification.object.widget.id);
        if (widget.length === 0) {
            return null;
        }
        return widget;
    }

    const storeNewTopicInTopicsDataAttr = (operation, widget) => {
        const mindmapElem = widget.find('.mindmap');
        const nodeId = operation.obj.id;
        const nodeTopics = JSON.parse(mindmapElem[0].dataset.nodeTopics);
        const sanitized = sanitizeTopic(operation.obj.topic);
        nodeTopics[nodeId] = sanitized;
        const mind = widget.data('mindmap-instance');
        if (sanitized !== operation.obj.topic) {
            mind.setNodeTopic(MindElixir.E(nodeId), sanitized);
        }
        mindmapElem.dataAndAttr('node-topics', JSON.stringify(nodeTopics));
        mindmapElem.dataAndAttr('node-string', JSON.parse(mind.getDataString()));
    };

    const restoreOriginalTopicsInNodeString = (mindmapElem) => {
        const nodeTopics = JSON.parse(mindmapElem[0].dataset.nodeTopics);
        const nodeDataString = mindmapElem[0].dataset.nodeString;
        let nodeData = parseNodeData(nodeDataString);

        const setOriginalTopic = (node) => {
            const nodeId = node.nodeData ? node.nodeData.id : node.id;
            // If the nodeId is in the nodeTopics, set the topic to the original topic.
            if (nodeTopics[nodeId]) {
                if (node.nodeData) {
                    node.nodeData.topic = nodeTopics[nodeId];
                } else {
                    node.topic = nodeTopics[nodeId];
                }
            }
            // If the node has children, recursively set the original topic for each child.
            if (node.nodeData ? node.nodeData.children : node.children) {
                (node.nodeData ? node.nodeData.children : node.children).forEach(child => {
                    setOriginalTopic(child);
                });
            }
        }

        // Recursively iterate over the node data and set the topic to the original topic from nodeTopics.
        if (nodeData) {
            setOriginalTopic(nodeData);
        }

        // Set the nodeData back to the mindmapElem.
        mindmapElem.dataAndAttr('node-string', JSON.stringify(nodeData));
    }

    const restoreOriginalTopic = (node, nodeTopicsString, mind) => {
        const nodeId = node.nodeObj.id;
        // Get the original topic from the data-node-topics.
        const nodeTopics = JSON.parse(nodeTopicsString);
        // Get the original topic from the nodeTopics.
        const originalTopic = nodeTopics[nodeId];
        mind.setNodeTopic(MindElixir.E(node.nodeObj.id), originalTopic);
    };

    const howspaceTheme = {
        name: 'Howspace',
        palette: [
            'var(--howspace-color-primary)',
        ],
        /**
         * CSS variables for the mindmap theme are duplicated in the mindmap CSS file.
         * They need to be defined here as well, so that the MindElixir library can use them.
         */
        cssVar: {
            '--root-color': 'var(--howspace-btn-primary-text-color);',
            '--root-bgcolor': 'var(--howspace-btn-primary-background-color);',
            '--main-color': 'var(--howspace-btn-secondary-text-color);',
            '--main-bgcolor': 'var(--howspace-btn-secondary-background-color);',
            '--color': 'var(--howspace-color-primary);',
            '--bgcolor': 'var(--howspace-color-widget-bg);',
            '--panel-color': 'var(--howspace-btn-tertiary-text-color);',
        }
    }

    /**
     * Parses the node data from the data-node-string attribute of the mindmap element.
     * Overrides the theme with the Howspace theme.
     */
    const parseNodeData = (nodeString) => {
        // Accept both JSON string and already-parsed object
        let nodeData = typeof nodeString === 'string' ? JSON.parse(nodeString) : nodeString;
        if (nodeData) {
            nodeData.theme = howspaceTheme;
        }
        return nodeData;
    }

    const initMindmap = async (widget) => {
        const mindmapElem = widget.find('.mindmap');
        if (mindmapElem.length === 0) {
            return;
        }

        await loadMindElixir();

        // If data hasn't arrived yet and loader is visible, don't initialize with a placeholder root.
        const container = widget.find('.mindmap-container');
        const loader = container.find('.howspace-loader');

        // Not enough source content yet: the blank state is shown, don't render a placeholder root.
        const noContent = container.find('.mindmap-no-content');
        if (noContent.length && !noContent.hasClass('d-none')) {
            return;
        }

        const editable = mindmapElem.data('editable');
        const customOptions = {
            editable: editable,
            draggable: editable
        };

        let language = document.documentElement.lang.split('-')[0];
        if (!locales[language]) {
            language = 'en';
        }

        const defaultOptions = {
            el: mindmapElem[0],
            direction: MindElixir.SIDE,
            draggable: true,
            contextMenu: {
                locale: locales[language]
            },
            toolBar: false,
            nodeMenu: true,
            keypress: true,
            editable: true,
            allowUndo: false,
            smooth: true,
            handleWheel: () => {
                // Disable the default wheel handler to prevent the scroll hijacking.
            },
            theme: howspaceTheme,
            before: {
                beginEdit: async function (node) {
                    restoreOriginalTopic(node, mindmapElem[0].dataset.nodeTopics, mind);
                    return true;
                }
            }
        };

        let options = {...defaultOptions, ...customOptions};
        const nodeDataString = mindmapElem[0].dataset.nodeString;

        // Avoid the root-only flash: if there is no node data yet and loader is shown, wait.
        if ((!nodeDataString || nodeDataString === '') && loader.length && loader.is(':visible')) {
            return;
        }

        let nodeData;
        if (nodeDataString) {
            nodeData = parseNodeData(nodeDataString);
        } else {
            nodeData = MindElixir.new(howspace.translate('mindmap_new_node_topic'))
        }

        let mind = widget.data('mindmap-instance');
        if (mind) {
            // If the mind is already initialized, refresh it with the new data.
            mind.refresh(nodeData);
        } else {
            // If mind hasn't been initialized, create a new instance.
            mind = new MindElixir(options);
            mind.init(nodeData);

            // Set operation listener, which posts the updated map to the server.
            mind.bus.addListener('operation', (operation) => {
                // If the operation name is not in the list, don't send it to the server.
                // For example, "beginEdit" should not be sent to the server.
                //console.log('operation', operation.name);

                // Handle finishEdit operation. Store the new topic in the data-node-topics.
                // If Translator is used, request a new translation.
                if (operation.name === 'finishEdit') {
                    storeNewTopicInTopicsDataAttr(operation, widget);
                }
                // Update the data-node-string attribute.
                mindmapElem.dataAndAttr('node-string', JSON.parse(mind.getDataString()));
                // Undo the translation of the node topic before sending it to the server.
                restoreOriginalTopicsInNodeString(mindmapElem);

                // Trigger the translator.
                howspace.components.translator.init(mindmapElem);

                // If this is not a sync operation, don't send it to the server.
                if (!syncOps.includes(operation.name)) {
                    return;
                }

                const dataPayload = {
                    version: MindElixir.version,
                    node_data: mindmapElem[0].dataset.nodeString
                };

                real.ajax.json(real.baseUri + '/api/widget/mindmap/' + widget.data('widget-id') + '/node-operation', {
                    type: 'POST',
                    data: JSON.stringify(dataPayload),
                });
            });
            // Store the instance in the widget data for later use.
            widget.data('mindmap-instance', mind);
        }

        scaleAndCentreToFit(widget)
    }

    const updateMindmap = (widget, notification) => {
        const mindmapElem = widget.find('.mindmap');
        if (mindmapElem.length === 0) {
            // The mindmap element is not found, ignore the notification.
            return;
        }

        widget.dataAndAttr('is-manually-edited', notification.object.widget.is_manually_edited);
        mindmapElem.dataAndAttr('node-string', JSON.parse(notification.object.widget.node_data));
        initMindmap(widget);
        setTimeout(() => {
            const mindmap = widget.find('.mindmap');
            mindmap.removeClass('translated');
            howspace.components.translator.init(mindmap);
        }, 1000)
    }

    const destroyMindmap = (widget) => {
        const mind = widget.data('mindmap-instance');
        if (mind) {
            mind.destroy();
            widget.removeData('mindmap-instance');
        }
    }

    // Source text is empty or too short to generate a mind map from. This is a state,
    // not an error: show the blank state instead of rendering. The widget will be
    // regenerated automatically once enough content appears.
    const handleMindmapNotEnoughContent = (notification) => {
        const widget = getWidgetFromNotification(notification);
        if (!widget) {
            return;
        }
        const container = widget.find('.mindmap-container');
        const loader = container.find('.howspace-loader');
        const mindmap = container.find('.mindmap');
        const noContent = container.find('.mindmap-no-content');

        destroyMindmap(widget);
        loader.addClass('d-none');
        mindmap.addClass('d-none');
        // With auto-update off the map will not regenerate on its own, so the copy
        // must not promise automatic generation.
        const messageKey = notification.object.is_auto_updated
            ? 'mindmap_not_enough_content'
            : 'mindmap_not_enough_content_manual';
        noContent.text(howspace.translate(messageKey));
        noContent.removeClass('d-none');

        howspace.aiGen.clearPromptTimeout(widget);
        real.button.reset(widget.find('.refresh-btn'));
    };

    const handleMindmapSourceEvent = (notification, isFailed = false) => {
        const widget = getWidgetFromNotification(notification);
        if (widget) {
            const container = widget.find('.mindmap-container');
            const loader = container.find('.howspace-loader');
            const mindmap = container.find('.mindmap');
            // Clear any previous blank state so the freshly generated map is shown.
            container.find('.mindmap-no-content').addClass('d-none');
            mindmap.removeClass('d-none');
            real.animator()
                .setParentElement(container)
                .setHideElement(loader)
                .setShowElement(mindmap)
                .setAnimDuration(350)
                .onShowReady(() => {
                    updateMindmap(widget, notification);
                    howspace.aiGen.clearPromptTimeout(widget);
                    howspace.components.translator.init(mindmap);
                    real.button.reset(widget.find('.refresh-btn'));
                    if (isFailed) {
                        if (notification.object?.error) {
                            real.notification.show(notification.object.error, 'danger');
                        } else {
                            real.notification.show(howspace.translate('mindmap_prompt_failed'), 'danger');
                        }
                    }
                }).startAnimation();
        }
    };

    real.events.on('mindmap:source:completed', (notification) => {
        if (notification.object?.not_enough_content) {
            handleMindmapNotEnoughContent(notification);
            return;
        }
        handleMindmapSourceEvent(notification);
    });

    real.events.on('mindmap:source:failed', (notification) => {
        handleMindmapSourceEvent(notification, true);
    });

    real.events.on('mindmap:node:operation', (notification) => {
        const widget = getWidgetFromNotification(notification);
        if (widget) {
            updateMindmap(widget, notification);
        }
    });

    const scaleAndCentreToFit = (widget) => {
        let mind = widget.data('mindmap-instance');
        if (mind) {
            mind.scaleFit();
            mind.toCenter();
        }
    };

    howspace.widgets.mindmap = {
        initView: widget => {
            initMindmap(widget);

            widget.find('.refresh-btn').on('click', () => {
                if (widget.data('is-manually-edited') !== true) {
                    real.ajax.json(real.baseUri + '/api/ai/mindmap/start/' + widget.data('widget-id'), {
                        type: 'POST',
                        showErrorNotification: hasServerErrorMessage,
                        success: () => {
                            howspace.aiGen.setPromptTimeout(widget);
                        },
                        error: (response) => {
                            if (!hasServerErrorMessage(response)) {
                                real.notification.show(howspace.translate('mindmap_start_failed'), 'danger');
                            }
                        },
                    });
                } else {
                    howspace.components.modal.show(howspace.widgets.getUrl(widget) + '/confirm-refresh-modal', (modal) => {
                        modal = $(modal);
                        modal.find('.btn-success').on('click', () => {
                            real.button.loading(widget.find('.refresh-btn'));
                            real.ajax.json(real.baseUri + '/api/ai/mindmap/start/' + widget.data('widget-id'), {
                                type: 'POST',
                                showErrorNotification: hasServerErrorMessage,
                                success: () => {
                                    howspace.aiGen.setPromptTimeout(widget);
                                },
                                error: (response) => {
                                    // On success the button is reset by the mindmap:source websocket events.
                                    // When the start request itself fails those events never arrive, so reset
                                    // here or the button would spin until page reload.
                                    real.button.reset(widget.find('.refresh-btn'));
                                    if (!hasServerErrorMessage(response)) {
                                        real.notification.show(howspace.translate('mindmap_start_failed'), 'danger');
                                    }
                                },
                                complete: () => {
                                    howspace.components.modal.close(modal);
                                }
                            });
                        });
                    });
                }
            });

            widget.find('.center-btn').on('click', () => {
                let mind = widget.data('mindmap-instance');
                if (mind) {
                    mind.scaleFit();
                    mind.toCenter();
                }
            })

            widget.find('.zoom-out-btn').on('click', () => {
                let mind = widget.data('mindmap-instance');
                if (mind) {
                    mind.scale(mind.scaleVal * 0.8);
                }
            });

            widget.find('.zoom-in-btn').on('click', () => {
                let mind = widget.data('mindmap-instance');
                if (mind) {
                    mind.scale(mind.scaleVal * 1.2);
                }
            });

            widget.on(howspace.fullscreen.FULLSCREEN_OPENED, () => {
                scaleAndCentreToFit(widget);
            });

            widget.on(howspace.fullscreen.FULLSCREEN_CLOSED, () => {
                scaleAndCentreToFit(widget);
            });

            widget.on(howspace.components.translator.TRANSLATION_COMPLETED, () => {
                initMindmap(widget);
                setTimeout(() => {
                    howspace.components.translator.init(widget.find('.mindmap'));
                }, 1000);
            });
            // If the widget is displaying a loader, disable the AI generation button.
            const loader = widget.find('.mindmap-container .howspace-loader');
            if (loader.length > 0 && loader.is(':visible')) {
                const btn = widget.find('.refresh-btn');
                real.button.loading(btn);
            }
        },
        destroyView: widget => {
            destroyMindmap(widget);
        },
        initEdit: widget => {
            widget.find('.save-widget-btn').on('click', e => {
                const initialSetup = widget.find('.mindmap-initial-setup');
                const isGenerateWithAi = initialSetup
                    .find('input[data-name="is_using_source_widget"]:checked')
                    .val() === 'true';
                const sourceSelect = initialSetup.find('select[name="source_widget_ref"]');
                if (isGenerateWithAi && !sourceSelect.val()) {
                    e.stopImmediatePropagation();
                    howspace.form.markInvalidField(sourceSelect, howspace.translate('common_required'));
                    sourceSelect.trigger('focus');
                    return false;
                }
                if (sourceSelect.length > 0) {
                    howspace.form.clearInvalidField(sourceSelect);
                }

                const hasInitialSetupChoice = initialSetup
                    .find('input[data-name="is_using_source_widget"]:checked').length > 0;
                if (widget.find('.mindmap-initial-setup').length === 0 || !hasInitialSetupChoice || MindElixir) {
                    return;
                }

                e.stopImmediatePropagation();
                const saveBtn = $(e.currentTarget);
                real.button.loading(saveBtn, widget);
                loadMindElixir()
                    .then(() => {
                        real.button.reset(saveBtn, widget);
                        saveBtn.trigger('click');
                    })
                    .catch(() => {
                        real.button.reset(saveBtn, widget);
                        real.notification.show(howspace.translate('mindmap_save_failed'), 'danger');
                    });
            });

            // Overwrite the save button action and abort the default action.
            widget.find('.save-widget-btn').on('click', e => {
                if (
                    widget.find('.mindmap-initial-setup').length === 0 &&
                    widget.find('textarea[name="user_prompt"]').data('user-prompt') !==
                    widget.find('textarea[name="user_prompt"]').val()
                ) {
                    e.stopImmediatePropagation();
                    howspace.components.modal.show(howspace.widgets.getUrl(widget) + '/confirm-save-and-refresh-modal', (modal) => {
                        modal = $(modal);
                        modal.find('.btn-success').on('click', () => {
                            real.button.loading(widget.find('.save-widget-btn'));
                            howspace.components.modal.close(modal);
                            widget.find('textarea[name="user_prompt"]').dataAndAttr('user-prompt', widget.find('textarea[name="user_prompt"]').val());
                            widget.find('.save-widget-btn').trigger('click');
                        });
                    });
                    return false;
                }
            });
        },
        destroyEdit: () => {
            // Nothing to do here.
        },
        save: (widget, dataPayload) => {
            if (widget.find('.mindmap-initial-setup').length > 0) {
                if (typeof dataPayload.is_using_source_widget === 'undefined') {
                    delete dataPayload.source_widget_ref;
                    return dataPayload;
                }

                const isUsingSourceWidget = dataPayload.is_using_source_widget === true ||
                    dataPayload.is_using_source_widget === 'true';
                dataPayload.version = MindElixir.version;
                dataPayload.is_using_source_widget = isUsingSourceWidget;

                if (isUsingSourceWidget) {
                    if (!dataPayload.source_widget_ref) {
                        return false;
                    }
                    const [sourceWidgetType, sourceWidgetId] = dataPayload.source_widget_ref.split('-');
                    dataPayload.source_widget_type = sourceWidgetType;
                    dataPayload.source_widget = sourceWidgetId;
                } else {
                    dataPayload.source_widget_type = null;
                    dataPayload.source_widget = null;
                    dataPayload.state = null;
                    dataPayload.node_data = JSON.stringify(
                        MindElixir.new(howspace.translate('mindmap_new_node_topic'))
                    );
                }
                delete dataPayload.source_widget_ref;
            }

            const mind = widget.data('mindmap-instance');
            if (mind) {
                dataPayload.version = MindElixir.version;
                dataPayload.node_data = mind.getDataString();
            }
            return dataPayload;
        },
    };
})();
